import { auditLogger, containsMaliciousPatterns, isValidUUID, sanitizeInput } from 'src/utils/security';
import { getSupabase } from 'src/utils/supabase';

import { profileService } from './profileService';
import { verifyOwnership, withSecurity } from './secureServiceWrapper';

export interface ClientNote {
  id: string;
  text: string;
  created_at: string;
  last_modified?: string;
  entity_id: string;
  client_id: string;
}

export const clientNotesService = {
  async listByClient(clientId: string): Promise<ClientNote[]> {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_client_id', service: 'clientNotes' });
          throw new Error('Invalid client ID');
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('client_notes')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: true });

        if (error) {
          throw error;
        }
        return data ?? [];
      },
      { requireAuth: true, rateLimitKey: 'client_notes_list' }
    );
  },

  async create(clientId: string, text: string): Promise<ClientNote> {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_client_id', service: 'clientNotes' });
          throw new Error('Invalid client ID');
        }

        const sanitizedText = sanitizeInput(text.trim());
        if (containsMaliciousPatterns(sanitizedText)) {
          auditLogger.log('MALICIOUS_INPUT_BLOCKED', { service: 'clientNotes', action: 'create' });
          throw new Error('Invalid input detected');
        }

        const profile = await profileService.getCurrentUserProfile();
        if (!profile) {
          throw new Error('User profile not found');
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('client_notes')
          .insert({
            client_id: clientId,
            entity_id: profile.id,
            text: sanitizedText,
          })
          .select()
          .single();

        if (error) {
          throw error;
        }
        return data;
      },
      { requireAuth: true, rateLimitKey: 'client_notes_create', rateLimitMax: 30 }
    );
  },

  async update(noteId: string, text: string): Promise<ClientNote> {
    return withSecurity(
      async () => {
        if (!isValidUUID(noteId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_note_id', service: 'clientNotes' });
          throw new Error('Invalid note ID');
        }

        const isOwner = await verifyOwnership('client_notes', noteId, 'entity_id');
        if (!isOwner) {
          throw new Error('You do not have permission to update this note');
        }

        const sanitizedText = sanitizeInput(text.trim());
        if (containsMaliciousPatterns(sanitizedText)) {
          auditLogger.log('MALICIOUS_INPUT_BLOCKED', { service: 'clientNotes', action: 'update' });
          throw new Error('Invalid input detected');
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('client_notes')
          .update({ text: sanitizedText, last_modified: new Date().toISOString() })
          .eq('id', noteId)
          .select()
          .single();

        if (error) {
          throw error;
        }
        return data;
      },
      { requireAuth: true, rateLimitKey: 'client_notes_update', rateLimitMax: 50 }
    );
  },

  async delete(noteId: string): Promise<void> {
    return withSecurity(
      async () => {
        if (!isValidUUID(noteId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_note_id', service: 'clientNotes' });
          throw new Error('Invalid note ID');
        }

        const isOwner = await verifyOwnership('client_notes', noteId, 'entity_id');
        if (!isOwner) {
          throw new Error('You do not have permission to delete this note');
        }

        const supabase = await getSupabase();
        const { error } = await supabase.from('client_notes').delete().eq('id', noteId);

        if (error) {
          throw error;
        }
      },
      { requireAuth: true, rateLimitKey: 'client_notes_delete', rateLimitMax: 20 }
    );
  },
};
